import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Title } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import * as echarts from 'echarts';

import { AuthService } from 'src/app/service/auth.service';
import {
  FileChannelsError,
  FileChannelsResult,
  FileChannelsService,
} from 'src/app/service/file-channels.service';
import { FileMapError, FileMapService } from 'src/app/service/file-map.service';
import {
  Compensations,
  FtFileType,
  InjectionMap,
  LogStatus,
} from 'src/app/model/response/file-map-response';
import { ChannelsListResponse, DisplayProjections } from 'src/app/model/response/log-display';
import { InjectionMap2dComponent } from './injection-map-2d.component';
import { InjectionMap3dComponent } from './injection-map-3d.component';
import { loadAxisLabel } from 'src/app/model/injection-map-orientation';
import { VISION_FT_CHANNEL_NAMES } from 'src/app/model/vision-ft-channels.model';
import { currentChannelLocale, resolveChannelLabel } from 'src/app/model/channel-label';
import { POWERFT_TO_VISIONFT, VISIONFT_TO_POWERFT } from 'src/app/model/vision-ft-channel-aliases';
import { buildDisplayProjections } from 'src/app/shared/log-display.util';
import { StatsPanelChannel } from './log-stats-panel.component';
import {
  formatTruncated,
  MAX_DISPLAY_DECIMALS,
  truncateTo,
} from 'src/app/shared/number-format.util';

/**
 * Como o mapa principal é exibido. `curve` = curva básica (`native_curve`, só quando o
 * mapa tem uma); `table` = grade de valores (o mapa em si);
 * `chart3d` = superfície 3D do mesmo mapa. NÃO confundir com o TIPO do mapa
 * (2D/3D) — ver `mapViewOptions`.
 */
type MapViewMode = 'curve' | 'table' | 'chart3d';

const NON_SELECTABLE = new Set<string>(['Sample_Time', 'Sample_Index']);

/** `error_code` do /map → chave i18n. Decidir sempre pelo código, nunca pelo texto de `message`. */
const MAP_ERROR_I18N: Record<string, string> = {
  FILE_NOT_FOUND: 'log.channels.map.errorFileNotFound',
  INVALID_JSON_SCHEMA: 'log.channels.map.errorInvalidRequest',
  CONVERSION_FAILED: 'log.channels.map.errorConversionFailed',
  INTERNAL_ERROR: 'log.channels.map.errorInternal',
  NO_MAP_FOUND: 'log.channels.map.errorNoMap',
  MAP_NOT_AVAILABLE: 'log.channels.map.errorNoMap',
  MAP_PASSWORD_PROTECTED: 'log.channels.map.errorPasswordProtected',
  MAP_PASSWORD_INVALID: 'log.channels.map.errorPasswordProtected',
  MAP_CONTAINER_INVALID: 'log.channels.map.errorContainerInvalid',
  MAP_DATA_UNAVAILABLE: 'log.channels.map.errorDataUnavailable',
  MAP_KEY_UNAVAILABLE: 'log.channels.map.errorKeyUnavailable',
  MAP_PASSWORD_ENVELOPE_INVALID: 'log.channels.map.errorEnvelopeInvalid',
  NETWORK_ERROR: 'log.channels.map.errorNetwork',
};
const MAX_CHANNELS = 10;
/** Valor mostrado quando a amostra está ausente (nulo no Arrow) — diferente de zero. */
const ABSENT_TEXT = '—';
const MAX_DECIMALS = MAX_DISPLAY_DECIMALS;
const MAX_GROUPS = 5;
const ZOOM_FACTOR = 0.5;

// Paleta de séries derivada do FT700 design system (docs/FT700_DESIGN_SYSTEM.md):
// chart accent (verde) + status colors + font (branco) + 2 tints derivados p/ 10 canais.
// Ordem espelha o Vision (verde → vermelho → laranja → amarelo → roxo → ...),
// mantendo os tokens de status do FT700. Assim o 1º canal (RPM) fica verde e o
// 2º (ex.: MAP) fica vermelho, como no desktop.
const VISION_PALETTE = [
  '#00FF00', // ft-chart          (verde — dado)
  '#FF0000', // vermelho puro     (MAP / 2º canal)
  '#FF8A00', // ft-status-orange
  '#F2C94C', // ft-status-yellow
  '#9B51E0', // ft-status-purple
  '#2D9CDB', // ft-status-blue
  '#27AE60', // ft-status-green
  '#FFFFFF', // ft-font
  '#7DBFFF', // tint derivado (azul claro)
  '#C88CF0', // tint derivado (roxo claro)
];

// Canais cuja unidade canônica (channel_metadata) é 'bar' podem ser exibidos em PSI. O
// toggle converte só a EXIBIÇÃO do gráfico (o /channels não aceita display_projections,
// contrato 2 §11.1): 1 bar = 14.5038 psi (§6), a forma da curva não muda, só os números e
// a escala do eixo. As estatísticas usam a projeção do servidor para os mesmos canais.
const BAR_TO_PSI = 14.5038;

/** Unidade esperada do canal de carga de cada eixo de mapa (MAP em bar, TPS em %). */
const SITE_LOAD_UNIT: Readonly<Record<string, string>> = { MAP: 'bar', TPS: '%' };

/** Candidatos a canal de RPM para o cursor do mapa, em ordem de preferência (nomes Vision). */
const SITE_RPM_CANDIDATES = ['RPM', 'RPM_Control', 'RPM_LoRes', 'RPM_HiRes'];

/** Formato de todo número desta tela: ponto decimal e sem agrupamento (contrato 2). */
const SCREEN_NUMBER_FORMAT = { locale: 'en-US', useGrouping: false } as const;

const INTEGER_CHANNEL_PATTERNS: RegExp[] = [
  /RPM/i,
  /Gear/i,
  /_Btn$/i,
  /_Button$/i,
  /_Signal$/i,
  /_Ok$/i,
  /_On$/i,
  /_Act$/i,
];

function defaultDecimalsFor(channel: string): number {
  return INTEGER_CHANNEL_PATTERNS.some((re) => re.test(channel)) ? 0 : 2;
}

@Component({
  selector: 'app-log-channels',
  templateUrl: './log-channels.component.html',
  styleUrls: ['./ft700-tokens.css', './log-channels.component.css'],
})
export class LogChannelsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chartRef') chartRef!: ElementRef<HTMLDivElement>;
  // Duas instâncias do 2D (grade e curva básica): por template ref, não por tipo.
  @ViewChild('map2dRef') map2dRef?: InjectionMap2dComponent;
  @ViewChild('mapCurveRef') mapCurveRef?: InjectionMap2dComponent;
  @ViewChild(InjectionMap3dComponent) map3dRef?: InjectionMap3dComponent;

  userID = '';
  fileID = '';
  fileType: FtFileType = 'ftlog';

  /** Mapa standalone (.ftm/.ftmap): não tem log — a tela vira "só mapa". */
  get isMapOnly(): boolean {
    return this.fileType === 'ftm' || this.fileType === 'ftmap';
  }

  /**
   * Catálogo selecionável. Vem do `channels_list` (nomes EXATOS do servidor, D6); só se essa
   * chamada falhar volta ao catálogo estático VisionFT, com tradução de alias no `.ftml`.
   */
  catalog: string[] = [];
  catalogLoading = false;
  /** Canais zerados (ou sem sinal finito) que o `channels_list` ainda oferece. */
  zeroedChannels = new Set<string>();
  private catalogFromList = false;
  private catalogReady = false;
  private viewReady = false;
  /** Unidade canônica por canal vinda do `channels_list` (null = desconhecida). */
  private listedUnits = new Map<string, string | null>();
  filterText = '';
  selectedChannels: string[] = [];
  channelColors = new Map<string, string>();
  channelDecimals = new Map<string, number>();
  channelGroups = new Map<string, number>();
  hiddenChannels = new Set<string>();
  /** Overrides de escala do eixo Y por canal (min/max). Sem entrada = usa o
   *  range real do dado (rawRange). Preenchido no diálogo de configuração. */
  channelRanges = new Map<string, { min: number; max: number }>();

  /** Unidade de exibição dos canais cuja unidade canônica é bar. Base do dado = bar;
   *  overrides de mín/máx são sempre guardados em bar. */
  mapUnit: 'bar' | 'psi' = 'bar';
  mapUnitOptions: Array<{ label: string; value: 'bar' | 'psi' }> = [
    { label: 'BAR', value: 'bar' },
    { label: 'PSI', value: 'psi' },
  ];

  /** Modal de configurações avançadas de UM canal (engrenagem / botão direito). */
  showChannelSettings = false;
  settingsChannel: string | null = null;
  /** Opções de grupo (1..MAX_GROUPS) para o select do modal por-canal. */
  groupOptions: Array<{ label: string; value: number }> = Array.from(
    { length: MAX_GROUPS },
    (_, i) => ({ label: `${i + 1}`, value: i + 1 }),
  );

  loading = false;
  error: string | null = null;
  hasData = false;
  /** Em tela estreita, após "Visualizar" manual: rola o gráfico à vista quando o dado chegar. */
  private scrollChartIntoViewOnResult = false;
  missing: string[] = [];
  rows = 0;
  showDecimalsDialog = false;

  /** Entradas do painel de estatísticas: refeitas só quando o resultado ou a seleção mudam. */
  statsChannels: StatsPanelChannel[] = [];
  statsProjections: DisplayProjections | null = null;

  // Estado do mapa de injeção (independente do estado do log)
  mapLoading = false;
  mapError: string | null = null;
  /** `error_code` por trás de `mapError` — decide ícone/ação do placeholder (ex.: senha). */
  mapErrorCode: string | null = null;
  /** Mapas por banco ativo: PowerFT 1 a 2 (A/B); Vision de 1 a 3 (A/B/C). */
  injectionMaps: InjectionMap[] = [];
  selectedMapIndex = 0;
  mapBankOptions: Array<{ label: string; value: number }> = [];
  readonly loadAxisLabel = loadAxisLabel;
  /**
   * Visualização do mapa principal: `curve` = a curva básica; `table` = a grade de valores;
   * `chart3d` = a superfície.
   * Os valores antigos eram `'2d'`/`'3d'`, o que confundia VISUALIZAÇÃO com TIPO DE MAPA —
   * ver `mapViewOptions`.
   */
  mapView: MapViewMode = 'table';
  /**
   * Compensações de injeção do envelope (contrato §4) — vêm para `.ftlog` E `.ftml`.
   * Guardadas aqui para não perder a parte do envelope; o render das tabelas é feature
   * à parte, mas a falha parcial já é avisada (§9 exige checar `errors.compensations`).
   */
  compensations: Compensations | null = null;
  /** Status da conversão do log (`s3_uri`/`ready`). `null` em `.ftm`/`.ftmap`. */
  logStatus: LogStatus | null = null;

  /**
   * Quando a seção do mapa aparece:
   * - `.ftm`/`.ftmap`: sempre — o mapa é o único conteúdo da tela;
   * - arquivo de log: só DEPOIS que os canais carregam (`hasData`). O mapa costuma chegar
   *   antes do `/channels` (são chamadas paralelas) e aparecer sozinho causava um estado
   *   intermediário estranho, com a área do log ainda vazia;
   * - exceção: se o log FALHOU e o mapa veio íntegro, a seção aparece de qualquer forma —
   *   contrato §6 (degradação graciosa: não esconder a parte que chegou). Sem isso um
   *   arquivo protegido por senha, por exemplo, não mostraria nem o mapa nem o motivo.
   */
  get showMapSection(): boolean {
    if (this.isMapOnly) return true;
    if (this.hasData) return true;
    return !!this.error && this.injectionMaps.length > 0;
  }

  /** Mapa do banco selecionado (o que está plotado em tela). */
  get injectionMap(): InjectionMap | null {
    return this.injectionMaps[this.selectedMapIndex] ?? null;
  }
  /**
   * Alternância de visualização do mapa principal.
   *
   * Rótulos NEUTROS de propósito: "Tabela" (a grade de valores) e "Gráfico 3D" (a
   * superfície do MESMO mapa). Os rótulos antigos ("2D"/"3D") sugeriam dois TIPOS de mapa,
   * o que é falso — é a mesma matriz em duas visualizações.
   *
   * O TIPO do mapa (2D/3D) NÃO é inferido pelo tamanho dos eixos (seria chute: um mapa
   * 17×3 é 2D na prática, mas os dois eixos têm mais de um ponto). Quem diz o que o mapa é
   * são `native_curve` (existe curva básica → "Curva básica" vira a primeira vista) e
   * `derivation` (a grade é calculada, não uma tabela 3D salva — vira nota no cabeçalho).
   *
   * ⚠️ Para as tabelas de COMPENSAÇÃO a regra é outra: existe campo próprio no contrato
   * (`dim`: 1 = curva, 2 = grade — §4). Lá o front deve LER o `dim`, nunca inferir.
   */
  get mapViewOptions(): Array<{ label: string; value: MapViewMode; icon: string }> {
    const lang = this.translocoService.getActiveLang();

    // MEMOIZAÇÃO OBRIGATÓRIA: este getter alimenta `[options]` do p-selectButton. Devolver
    // um array novo a cada change detection faz o Angular ver a binding "mudando" entre as
    // duas passadas do modo dev (ExpressionChangedAfterItHasBeenCheckedError) e o botão para
    // de responder ao clique. Só recria quando o idioma ou a existência de curva muda.
    const hasCurve = !!this.injectionMap?.native_curve;
    const key = `${lang}|${hasCurve}`;
    if (this.mapViewOptionsCache?.key !== key) {
      const t = (k: string) => this.translocoService.translate(`log.channels.${k}`);
      this.mapViewOptionsCache = {
        key,
        options: [
          ...(hasCurve
            ? [{ label: this.translocoService.translate('injectionMap.nativeCurve'), value: 'curve' as MapViewMode, icon: 'pi pi-chart-line' }]
            : []),
          { label: t('mapTable'), value: 'table' as MapViewMode, icon: 'pi pi-th-large' },
          { label: t('mapChart3d'), value: 'chart3d' as MapViewMode, icon: 'pi pi-box' },
        ],
      };
    }
    return this.mapViewOptionsCache.options;
  }

  private mapViewOptionsCache: {
    key: string;
    options: Array<{ label: string; value: MapViewMode; icon: string }>;
  } | null = null;

  /** O que está visível em tela: log + mapa, só log ou só mapa. */
  displayMode: 'both' | 'log' | 'map' = 'both';
  displayModeOptions: Array<{ label: string; value: 'both' | 'log' | 'map'; icon: string }> = [
    { label: 'Log + Mapa', value: 'both', icon: 'pi pi-clone' },
    { label: 'Só Log',     value: 'log',  icon: 'pi pi-chart-line' },
    { label: 'Só Mapa',    value: 'map',  icon: 'pi pi-th-large' },
  ];

  // Canais "de sítio" (RPM + eixo do mapa) carregados em background pra alimentar
  // o cursor de operação no mapa. NÃO entram no gráfico do log; servem só pra
  // mapear o pixel do mouse → (rpm, load) em tempo real.
  private siteSampleTime: Float32Array | null = null;
  private siteRpm: ArrayLike<number> | null = null;
  private siteLoad: ArrayLike<number> | null = null;
  private siteRafId: number | null = null;
  /** Eixo de carga (MAP/TPS) do banco para o qual os canais de sítio foram carregados. */
  private siteAxis: string | null = null;

  // Toolbar / view modes
  groupedView = false;
  followCursor = true;

  // Right-click context menu state
  contextMenuOpen = false;
  contextMenuX = 0;
  contextMenuY = 0;
  contextMenuChartX: number | null = null;

  // Inline labels / fixed cursors
  showCursorNames = false;
  showCursorValues = false;
  cursorsVisible = true;
  cursor1X: number | null = null;
  cursor2X: number | null = null;
  lastHoverX: number | null = null;

  private chartInstance: echarts.ECharts | null = null;
  private destroy$ = new Subject<void>();
  private lastResult: FileChannelsResult | null = null;

  // ─── Mouse drag state (left=area zoom, right=horizontal pan) ───
  private dragMode: 'none' | 'zoom' | 'pan' = 'none';
  private dragStartPx = 0;
  private dragCurrentPx = 0;
  private dragStartRangeStart = 0;
  private dragStartRangeEnd = 100;
  private dragSelectionEl: HTMLDivElement | null = null;
  private readonly DRAG_THRESHOLD_PX = 4;

  readonly maxChannels = MAX_CHANNELS;
  readonly maxDecimals = MAX_DECIMALS;
  readonly maxGroups = MAX_GROUPS;
  readonly groupList = Array.from({ length: MAX_GROUPS }, (_, i) => i + 1);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private channelsService: FileChannelsService,
    private mapService: FileMapService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private titleService: Title,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.userID = this.authService.getUserInformations();
    this.fileID = this.route.snapshot.paramMap.get('fileID') ?? '';
    const typeParam = (this.route.snapshot.queryParamMap.get('type') ?? '').toLowerCase();
    this.fileType = (['ftml', 'ftm', 'ftmap'] as const).find((t) => t === typeParam) ?? 'ftlog';

    this.translocoService
      .selectTranslate(this.isMapOnly ? 'log.channels.titleMapOnly' : 'log.channels.title')
      .pipe(takeUntil(this.destroy$))
      .subscribe((label) => this.titleService.setTitle(`FT Pilot - ${label}`));

    // Rótulos do painel de estatísticas são resolvidos uma vez; ao trocar o idioma precisam ser refeitos.
    this.translocoService.langChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { if (this.statsChannels.length > 0) this.refreshStatsInputs(); });

    // Mapa standalone: sem log pra plotar — trava a tela em "só mapa" e já
    // dispara a leitura do mapa (no fluxo de log isso só acontece no 1º View).
    if (this.isMapOnly) {
      this.displayMode = 'map';
      this.loadMap();
    } else {
      // Log: o catálogo vem do channels_list; quando ele chega o RPM do motor já entra
      // selecionado e o gráfico é plotado (ver finishCatalog).
      this.loadCatalog();
    }

    this.renderer.addClass(document.body, 'log-channels-page-active');
  }

  /**
   * Catálogo de canais do arquivo (contrato 2 §11): `available_channels` ∪ `zeroed_channels`,
   * sem Sample_Time/Sample_Index. Os zerados continuam selecionáveis (só ganham uma dica).
   */
  private loadCatalog(): void {
    this.catalogLoading = true;
    this.channelsService
      .listChannels(this.userID, this.fileID, this.channelsFileType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => this.applyChannelsList(list),
        error: () => this.applyFallbackCatalog(),
      });
  }

  private applyChannelsList(list: ChannelsListResponse): void {
    const available = list.available_channels ?? [];
    const zeroed = list.zeroed_channels ?? [];
    this.catalog = [...new Set([...available.map((c) => c.channel), ...zeroed])].filter(
      (c) => !NON_SELECTABLE.has(c),
    );
    this.zeroedChannels = new Set(zeroed);

    this.listedUnits.clear();
    for (const [channel, meta] of Object.entries(list.channel_metadata ?? {})) {
      this.listedUnits.set(channel, meta.unit ?? null);
    }
    for (const c of available) this.listedUnits.set(c.channel, c.unit ?? null);

    this.catalogFromList = true;
    this.finishCatalog();
  }

  /** `channels_list` falhou: catálogo VisionFT estático, com a tradução de alias no `.ftml`. */
  private applyFallbackCatalog(): void {
    this.catalog = VISION_FT_CHANNEL_NAMES.filter((c) => !NON_SELECTABLE.has(c));
    this.zeroedChannels = new Set();
    this.listedUnits.clear();
    this.catalogFromList = false;
    this.finishCatalog();
  }

  /**
   * O RPM do motor já entra selecionado (com cor, casas e grupo padrão) — é o canal que todo
   * mundo plota primeiro. Passa por toggleChannel pra não divergir da seleção manual. Com o
   * `channels_list` escolhe o RPM que existe no arquivo: `RPM` na Vision, o alias PowerFT no `.ftml`.
   */
  private finishCatalog(): void {
    this.catalogLoading = false;
    const rpm = this.catalogFromList
      ? [...new Set(['RPM', VISIONFT_TO_POWERFT['RPM']])].find((c) => this.catalog.includes(c))
      : 'RPM';
    if (rpm) this.toggleChannel(rpm);
    this.catalogReady = true;
    this.startInitialPlot();
  }

  /**
   * Abrir um log já plota o RPM pré-selecionado e dispara o mapa, sem exigir "Visualizar".
   * Precisa do gráfico inicializado (ngAfterViewInit) E do catálogo resolvido: só a segunda
   * dessas duas chamadas passa pela condição.
   */
  private startInitialPlot(): void {
    if (this.isMapOnly || !this.viewReady || !this.catalogReady) return;
    if (this.selectedChannels.length > 0) {
      // O plot já dispara o loadMap quando o mapa ainda não foi pedido.
      this.plot();
    } else if (!this.injectionMap && !this.mapLoading && !this.mapError) {
      // Sem canal pré-selecionado (ex.: sem RPM no arquivo) o mapa não depende do gráfico.
      this.loadMap();
    }
  }

  /** `channels_list` resolvido e o arquivo não tem nenhum canal: estado próprio, não "escolha canais". */
  get catalogEmpty(): boolean {
    return this.catalogReady && this.catalogFromList && this.catalog.length === 0;
  }

  /** Público porque o placeholder "protegido por senha" oferece "Informar senha", que é só reler. */
  loadMap(): void {
    if (!this.userID || !this.fileID) return;
    this.mapLoading = true;
    this.mapError = null;
    this.mapErrorCode = null;
    this.injectionMaps = [];
    this.selectedMapIndex = 0;
    this.mapBankOptions = [];
    // Um erro nesta leitura não pode deixar o status e as compensações da leitura anterior.
    this.logStatus = null;
    this.compensations = null;

    this.mapService.read(this.userID, this.fileID, this.fileType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          this.mapLoading = false;
          // Falha parcial: mapa veio mas a conversão do log falhou (200 com
          // errors.log preenchido). Avisa cedo — sem isso o usuário só
          // descobre quando o /channels falhar. Em .ftm/.ftmap log null é
          // esperado e errors.log não vem.
          if (!this.isMapOnly && env.errors?.log) {
            this.messageService.add({
              key: 'notification',
              severity: 'warn',
              summary: this.translocoService.translate('log.channels.toast.logUnavailable'),
              detail: env.errors.log.message
                || this.translocoService.translate('log.channels.toast.logUnavailableDefault'),
            });
          }
          // Partes do envelope que antes eram descartadas (§9: checar TODAS as falhas parciais).
          this.logStatus = env.log ?? null;
          this.compensations = env.compensations ?? null;
          if (env.errors?.compensations) {
            this.messageService.add({
              key: 'notification',
              severity: 'warn',
              summary: this.translocoService.translate('log.channels.toast.compensationsUnavailable'),
              detail: env.errors.compensations.message
                || this.translocoService.translate('log.channels.toast.compensationsUnavailableDefault'),
            });
          }

          if (env.maps && env.maps.length > 0) {
            this.injectionMaps = env.maps;
            this.mapView = this.injectionMap?.native_curve ? 'curve' : 'table';
            this.mapBankOptions = env.maps.map((m, i) => ({
              label: this.translocoService.translate('injectionMap.bank', { bank: m.bank }),
              value: i,
            }));
            // Após receber o mapa, carrega os canais de RPM + eixo de carga em
            // background pra alimentar o cursor de operação. Cache-hit no
            // parquet, então é uma chamada rápida. (Sem log em .ftm/.ftmap.)
            this.ensureSiteChannels();
          } else {
            this.mapErrorCode = env.errors?.map?.error_code ?? null;
            this.mapError = this.friendlyMapError(env.errors?.map);
          }
        },
        error: (err: FileMapError) => {
          this.mapLoading = false;
          this.mapErrorCode = err.errorCode;
          this.mapError = this.friendlyMapError(err);
        },
      });
  }

  /**
   * Usuário cancelou o modal de senha (ou a senha continua faltando): o placeholder do mapa
   * mostra o cadeado e um botão para tentar de novo — sem isso a tela ficava num beco sem saída
   * até o reload. O log segue funcionando, a senha só trava o mapa.
   */
  get mapPasswordProtected(): boolean {
    return this.mapErrorCode === 'MAP_PASSWORD_PROTECTED' || this.mapErrorCode === 'MAP_PASSWORD_INVALID';
  }

  /**
   * Tipo aceito pelo /channels — só a família de log. Nunca chamar canais em
   * `.ftm`/`.ftmap` (guardado por `isMapOnly` nos call-sites).
   */
  private get channelsFileType(): 'ftml' | 'ftlog' {
    return this.fileType === 'ftml' ? 'ftml' : 'ftlog';
  }

  /**
   * Busca o RPM e o canal de carga do mapa (MAP/TPS) pra usar no cursor sincronizado.
   * Com o `channels_list` os nomes saem do catálogo, exatos; sem ele (fallback) valem os nomes
   * VisionFT traduzidos como no gráfico. O cursor só liga quando a unidade do canal de carga é
   * compatível com o eixo do mapa (MAP em bar, TPS em %) ou ainda desconhecida: um MAP em outra
   * unidade cairia na célula errada sem aviso.
   */
  private ensureSiteChannels(): void {
    if (this.isMapOnly) return; // mapa standalone não tem log → sem cursor
    if (!this.injectionMap || !this.userID || !this.fileID) return;
    this.resetSiteChannels();
    const loadBase = this.injectionMap.axis; // ex.: "MAP" ou "TPS"
    this.siteAxis = loadBase;

    let rpmCandidates: string[];
    let loadName: string;
    if (this.catalogFromList) {
      const inCatalog = (base: string) =>
        [base, VISIONFT_TO_POWERFT[base]].find((c) => !!c && this.catalog.includes(c));
      const rpm = SITE_RPM_CANDIDATES.map(inCatalog).find((c) => !!c);
      const load = inCatalog(loadBase);
      if (!rpm || !load) return; // o arquivo não tem os canais: mapa estático, sem cursor
      rpmCandidates = [rpm];
      loadName = load;
    } else {
      rpmCandidates = SITE_RPM_CANDIDATES.map((c) => this.serverNameFor(c));
      loadName = this.serverNameFor(loadBase);
    }
    const need = [...new Set<string>([...rpmCandidates, loadName])];

    // Sem lttb pro sync ser mais preciso. Se o backend tiver downsampled, ainda
    // funciona — só perde resolução temporal.
    this.channelsService.read(this.userID, this.fileID, need, { fileType: this.channelsFileType })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const unit = result.units[loadName] ?? this.listedUnits.get(loadName) ?? null;
          const expected = SITE_LOAD_UNIT[loadBase];
          if (unit !== null && expected !== undefined && unit !== expected) return;
          const rpmName = rpmCandidates.find((c) => !!result.series[c]);
          if (!rpmName || !result.series[loadName]) return;
          this.siteSampleTime = result.sampleTime;
          this.siteRpm = result.series[rpmName];
          this.siteLoad = result.series[loadName];
        },
        error: () => {
          // Falha silenciosa — sem cursor sync, mas mapa estático continua OK
          this.resetSiteChannels();
        },
      });
  }

  private resetSiteChannels(): void {
    this.siteAxis = null;
    this.siteSampleTime = null;
    this.siteRpm = null;
    this.siteLoad = null;
  }

  /** Hover sobre o chart do log → projeta a posição no mapa de injeção. */
  onLogHover(pixelX: number): void {
    if (!this.chartInstance || !this.injectionMap) return;
    if (!this.siteSampleTime || !this.siteRpm || !this.siteLoad) return;

    // Throttle por requestAnimationFrame — coalesce múltiplos eventos em 1 update
    if (this.siteRafId !== null) cancelAnimationFrame(this.siteRafId);
    this.siteRafId = requestAnimationFrame(() => {
      this.siteRafId = null;
      if (!this.chartInstance) return;
      // Converte pixel X do mouse em valor do eixo X (segundos desde início do log)
      const t = this.chartInstance.convertFromPixel({ xAxisIndex: 0 }, pixelX);
      if (typeof t !== 'number' || !Number.isFinite(t)) return;
      const idx = this.nearestTimeIndex(t);
      if (idx < 0) return;
      const rpm  = Number(this.siteRpm![idx]);
      const load = Number(this.siteLoad![idx]);
      if (!Number.isFinite(rpm) || !Number.isFinite(load)) return;
      this.map2dRef?.setSite(load, rpm);
      this.mapCurveRef?.setSite(load, rpm);
      this.map3dRef?.setSite(load, rpm);
    });
  }

  /** Limpa os marcadores do mapa (chamado no globalout/mouseleave do chart). */
  clearLogHover(): void {
    if (this.siteRafId !== null) {
      cancelAnimationFrame(this.siteRafId);
      this.siteRafId = null;
    }
    this.map2dRef?.clearSite();
    this.mapCurveRef?.clearSite();
    this.map3dRef?.clearSite();
  }

  /** Busca binária pelo sample com tempo mais próximo de `t`. */
  private nearestTimeIndex(t: number): number {
    const arr = this.siteSampleTime;
    if (!arr || arr.length === 0) return -1;
    let lo = 0, hi = arr.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (arr[mid] < t) lo = mid + 1;
      else hi = mid;
    }
    // ajusta pro vizinho mais próximo entre lo e lo-1
    if (lo > 0 && Math.abs(arr[lo - 1] - t) < Math.abs(arr[lo] - t)) return lo - 1;
    return lo;
  }

  /** Troca de banco (Vision multi-banco): os componentes 2D/3D reagem via
   *  OnChanges; só força o resize do que está visível após o reflow. Se a nova
   *  bancada não tem curva básica, a vista 'curve' deixa de existir e volta p/ tabela. */
  onBankChange(): void {
    if (this.mapView === 'curve' && !this.injectionMap?.native_curve) this.mapView = 'table';
    // Outro banco pode ter outro eixo de carga: o cursor precisa do canal certo (MAP x TPS).
    if (this.injectionMap && this.injectionMap.axis !== this.siteAxis) this.ensureSiteChannels();
    setTimeout(() => this.resizeVisibleMap(), 0);
  }

  /** Após trocar entre 2D/3D, força o resize do echart que acabou de ficar visível
   *  (estava escondido via display:none, então tinha tamanho 0). */
  onMapViewChange(): void {
    setTimeout(() => this.resizeVisibleMap(), 0);
  }

  /** Resize do chart do mapa que está na vista atual. */
  private resizeVisibleMap(): void {
    if (this.mapView === 'curve') this.mapCurveRef?.resize();
    else if (this.mapView === 'table') this.map2dRef?.resize();
    else this.map3dRef?.resize();
  }

  /** Troca o modo de visualização (log+mapa / só log / só mapa) e força resize
   *  dos charts depois do reflow do CSS. */
  onDisplayModeChange(): void {
    setTimeout(() => {
      // Chart do log
      if (this.displayMode !== 'map') this.chartInstance?.resize();
      // Chart do mapa visível
      if (this.displayMode !== 'log') this.resizeVisibleMap();
    }, 0);
  }

  /**
   * Normaliza erros do mapa em mensagem amigável.
   * Aceita: `FileMapError` (do http throw), `PartError` (`env.errors.map`,
   * objeto `{error_code, message, context}` do contrato), string (code cru
   * de versões antigas), ou undefined/null.
   */
  private friendlyMapError(raw: unknown): string {
    if (raw === null || raw === undefined) {
      return this.translocoService.translate('log.channels.map.errorUnavailable');
    }

    if (typeof raw === 'string') {
      return this.mapErrorCodeToMessage(raw) ?? raw;
    }

    if (typeof raw === 'object') {
      const obj = raw as { errorCode?: string; error_code?: string; code?: string; message?: string };
      const code = obj.errorCode ?? obj.error_code ?? obj.code;
      if (code) {
        const friendly = this.mapErrorCodeToMessage(code);
        if (friendly) return friendly;
      }
      if (typeof obj.message === 'string' && obj.message.trim()) {
        return obj.message;
      }
    }

    return this.translocoService.translate('log.channels.map.errorUnavailable');
  }

  /** Códigos do contrato map_log + os de senha (VISIONFT/POWERFT_PASSWORD_FRONT_CONTRACT §2). */
  private mapErrorCodeToMessage(code: string): string | null {
    const key = MAP_ERROR_I18N[code];
    return key ? this.translocoService.translate(key) : null;
  }

  ngAfterViewInit(): void {
    this.chartInstance = echarts.init(this.chartRef.nativeElement);
    this.bindMouseDragHandlers();
    // A plotagem inicial depende do chart inicializado acima e do catálogo (loadCatalog).
    // Mudanças de seleção depois disso continuam manuais.
    this.viewReady = true;
    this.startInitialPlot();
  }

  private bindMouseDragHandlers(): void {
    if (!this.chartInstance) return;
    // Native DOM mousedown in capture phase — gives us 100% reliable `event.button`
    // straight from the browser, before any ECharts/ZRender normalization kicks in.
    // (Previously we used zr.on('mousedown', ...) and the button value was sometimes
    //  inconsistent — right-click would occasionally arrive as left.)
    this.chartRef.nativeElement.addEventListener('mousedown', this.onDomMouseDown, { capture: true });
    // ZRender for in-canvas mousemove tracking (reliable while cursor is over canvas).
    const zr = this.chartInstance.getZr();
    zr.on('mousemove', this.onZrMouseMove);
    zr.on('mouseup', this.onZrMouseUp);
    // globalout = mouse saiu da área do chart → limpa os marcadores do mapa
    this.chartInstance.on('globalout', this.onChartGlobalOut);
    // window fallback so drag survives when the cursor leaves the canvas mid-drag.
    window.addEventListener('mousemove', this.onWindowMouseMove);
    window.addEventListener('mouseup', this.onWindowMouseUp);
    // Suppress OS-level context menu over the chart.
    this.chartRef.nativeElement.addEventListener('contextmenu', this.onNativeContextMenu);
  }

  private unbindMouseDragHandlers(): void {
    this.chartRef?.nativeElement?.removeEventListener('mousedown', this.onDomMouseDown, { capture: true } as EventListenerOptions);
    try {
      const zr = this.chartInstance?.getZr();
      zr?.off('mousemove', this.onZrMouseMove);
      zr?.off('mouseup', this.onZrMouseUp);
      this.chartInstance?.off('globalout', this.onChartGlobalOut);
    } catch { /* zr disposed */ }
    window.removeEventListener('mousemove', this.onWindowMouseMove);
    window.removeEventListener('mouseup', this.onWindowMouseUp);
    this.chartRef?.nativeElement?.removeEventListener('contextmenu', this.onNativeContextMenu);
  }

  private onNativeContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private onDomMouseDown = (event: MouseEvent): void => {
    if (!this.chartInstance || !this.hasData) return;
    // Right button does NOT initiate drag — it only opens the in-app context menu
    // (handled by the template's (contextmenu) binding).
    if (event.button !== 0) return;

    const rect = this.chartRef.nativeElement.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    // Skip our custom drag if the click landed on the bottom slider (dataZoom type:
    // 'slider' is configured with height: 28 + bottom: 12 = ~40px). The ECharts slider
    // handles its own drag/resize natively — we don't want to interfere.
    const sliderZoneTop = rect.height - 50;
    if (offsetY >= sliderZoneTop) return;

    // Defensive cleanup: never carry over residual state from a previous drag.
    this.removeSelectionOverlay();

    this.dragStartPx = offsetX;
    this.dragCurrentPx = offsetX;

    const opt = this.chartInstance.getOption() as { dataZoom?: Array<Record<string, unknown>> };
    const dz = (opt.dataZoom ?? [])[0] ?? {};
    this.dragStartRangeStart = typeof dz['start'] === 'number' ? (dz['start'] as number) : 0;
    this.dragStartRangeEnd = typeof dz['end'] === 'number' ? (dz['end'] as number) : 100;

    // Ctrl (or Cmd on macOS) + left drag → area zoom. Plain left drag → pan.
    if (event.ctrlKey || event.metaKey) {
      this.dragMode = 'zoom';
      this.createSelectionOverlay(offsetX);
    } else {
      this.dragMode = 'pan';
    }
  };

  private onZrMouseMove = (params: { offsetX: number; event?: MouseEvent }): void => {
    this.processDragMove(params.offsetX, params.event?.buttons);
    // Sincroniza o cursor de operação no mapa quando NÃO estamos arrastando
    if (this.dragMode === 'none') this.onLogHover(params.offsetX);
  };

  private onChartGlobalOut = (): void => {
    this.clearLogHover();
  };

  private onWindowMouseMove = (event: MouseEvent): void => {
    if (this.dragMode === 'none') return;
    const rect = this.chartRef.nativeElement.getBoundingClientRect();
    this.processDragMove(event.clientX - rect.left, event.buttons);
  };

  private processDragMove(offsetX: number, buttons?: number): void {
    if (this.dragMode === 'none' || !this.chartInstance) return;

    // If the user released the left button outside our listeners somehow,
    // finalize the drag (defensive — drag is only ever started by left button now).
    if (buttons !== undefined && (buttons & 1) === 0) {
      this.finishDrag();
      return;
    }

    this.dragCurrentPx = offsetX;
    if (this.dragMode === 'zoom') {
      this.updateSelectionOverlay();
    } else if (this.dragMode === 'pan') {
      this.applyPanDrag();
    }
  }

  private onZrMouseUp = (): void => {
    this.finishDrag();
  };

  private onWindowMouseUp = (_event: MouseEvent): void => {
    if (this.dragMode === 'none') return;
    this.finishDrag();
  };

  private finishDrag(): void {
    if (this.dragMode === 'none') return;
    const delta = Math.abs(this.dragCurrentPx - this.dragStartPx);
    const mode = this.dragMode;
    this.dragMode = 'none';

    if (mode === 'zoom') {
      this.removeSelectionOverlay();
      if (delta >= this.DRAG_THRESHOLD_PX) this.applyZoomFromSelection();
    }
    // mode === 'pan' has no post-drag side effect now (pan was applied live in mousemove).
  }

  private createSelectionOverlay(startPx: number): void {
    if (this.dragSelectionEl) this.dragSelectionEl.remove();
    const el = document.createElement('div');
    el.className = 'chart-drag-selection';
    el.style.position = 'absolute';
    el.style.top = '0';
    el.style.bottom = '34px'; // matches dataZoom slider area at bottom: 12 + height 28 - small overlap
    el.style.left = `${startPx}px`;
    el.style.width = '0px';
    el.style.background = 'rgba(0, 255, 0, 0.18)';
    el.style.border = '1px solid #00FF00';
    el.style.pointerEvents = 'none';
    el.style.zIndex = '5';
    this.chartRef.nativeElement.style.position = 'relative';
    this.chartRef.nativeElement.appendChild(el);
    this.dragSelectionEl = el;
  }

  private updateSelectionOverlay(): void {
    if (!this.dragSelectionEl) return;
    const left = Math.min(this.dragStartPx, this.dragCurrentPx);
    const width = Math.abs(this.dragCurrentPx - this.dragStartPx);
    this.dragSelectionEl.style.left = `${left}px`;
    this.dragSelectionEl.style.width = `${width}px`;
  }

  private removeSelectionOverlay(): void {
    this.dragSelectionEl?.remove();
    this.dragSelectionEl = null;
  }

  private applyZoomFromSelection(): void {
    if (!this.chartInstance) return;
    const startPx = Math.min(this.dragStartPx, this.dragCurrentPx);
    const endPx = Math.max(this.dragStartPx, this.dragCurrentPx);
    const startVal = this.chartInstance.convertFromPixel({ xAxisIndex: 0 }, startPx);
    const endVal = this.chartInstance.convertFromPixel({ xAxisIndex: 0 }, endPx);
    if (typeof startVal !== 'number' || typeof endVal !== 'number') return;
    if (startVal === endVal) return;
    this.chartInstance.dispatchAction({
      type: 'dataZoom',
      startValue: Math.min(startVal, endVal),
      endValue: Math.max(startVal, endVal),
    });
  }

  private applyPanDrag(): void {
    if (!this.chartInstance) return;
    const chartWidth = this.chartRef.nativeElement.clientWidth || 1;
    const deltaPx = this.dragCurrentPx - this.dragStartPx;
    const rangeSize = this.dragStartRangeEnd - this.dragStartRangeStart;
    // Inverted: drag right pans content left (i.e. shifts window to the left = sees earlier data)
    const deltaPct = (deltaPx / chartWidth) * rangeSize;
    let newStart = this.dragStartRangeStart - deltaPct;
    let newEnd = this.dragStartRangeEnd - deltaPct;
    if (newStart < 0) { newEnd -= newStart; newStart = 0; }
    if (newEnd > 100) { newStart -= (newEnd - 100); newEnd = 100; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(100, newEnd);
    this.chartInstance.dispatchAction({ type: 'dataZoom', start: newStart, end: newEnd });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unbindMouseDragHandlers();
    this.removeSelectionOverlay();
    this.chartInstance?.dispose();
    this.chartInstance = null;
    this.renderer.removeClass(document.body, 'log-channels-page-active');
  }

  @HostListener('window:resize')
  onResize(): void {
    this.chartInstance?.resize();
  }

  get filteredChannels(): string[] {
    const q = this.filterText.trim().toLowerCase();
    if (!q) return this.catalog;
    return this.catalog.filter(
      (c) => c.toLowerCase().includes(q) || this.labelFor(c).toLowerCase().includes(q),
    );
  }

  /**
   * Rótulo do canal.
   *
   * DIFERENÇA PROPOSITAL em relação ao gráfico do chat: quando não há tradução, aqui
   * mantemos o nome técnico em vez do rótulo genérico. Nesta tela o usuário ESCOLHE o canal
   * numa lista de ~1080 itens (467 sem tradução) e é pelo nome técnico que ele identifica e
   * busca o canal — trocar todos por "Canal não identificado" tornaria o seletor inútil.
   *
   * Nota: o ramo de dedup PowerFT (`RPMIndex_2`) do helper não se aplica a esta tela — o
   * catálogo aqui é só de nomes VisionFT e `normalizeAliasedResult` já traz as colunas
   * renomeadas para VisionFT antes de qualquer render.
   */
  labelFor(channel: string): string {
    const locale = currentChannelLocale();
    const r = resolveChannelLabel(channel, locale);
    if (r.resolved) return r.label;
    // Nome PowerFT exato do servidor: o rótulo vem do nome VisionFT equivalente (só para exibir).
    const vision = POWERFT_TO_VISIONFT[channel];
    if (vision) {
      const v = resolveChannelLabel(vision, locale);
      if (v.resolved) return v.label;
    }
    return r.raw;
  }

  /** Canal que o `channels_list` marcou como zerado (selecionável, com dica). */
  isZeroed(channel: string): boolean {
    return this.zeroedChannels.has(channel);
  }

  /** Nomes de canal já traduzidos, para mensagens (banner/toast) que antes saíam cruas. */
  get missingLabels(): string {
    return this.missing.map((c) => this.labelFor(c)).join(', ');
  }

  get selectionFull(): boolean {
    return this.selectedChannels.length >= MAX_CHANNELS;
  }

  isSelected(channel: string): boolean {
    return this.channelColors.has(channel);
  }

  isDisabled(channel: string): boolean {
    return !this.isSelected(channel) && this.selectionFull;
  }

  toggleChannel(channel: string): void {
    if (this.isSelected(channel)) {
      this.channelColors.delete(channel);
      this.channelDecimals.delete(channel);
      this.channelGroups.delete(channel);
      this.channelRanges.delete(channel);
      this.hiddenChannels.delete(channel);
      this.selectedChannels = this.selectedChannels.filter((c) => c !== channel);
      return;
    }
    if (this.selectionFull) return;
    this.selectedChannels.push(channel);
    this.channelColors.set(channel, this.nextAvailableColor());
    if (!this.channelDecimals.has(channel)) {
      this.channelDecimals.set(channel, defaultDecimalsFor(channel));
    }
    if (!this.channelGroups.has(channel)) {
      this.channelGroups.set(channel, 1);
    }
  }

  clearSelection(): void {
    this.selectedChannels = [];
    this.channelColors.clear();
    this.channelDecimals.clear();
    this.channelGroups.clear();
    this.channelRanges.clear();
    this.hiddenChannels.clear();
  }

  /** Reordena os canais selecionados (drag-drop na sidebar). A ordem passa a
   *  controlar o empilhamento dos eixos Y no gráfico. */
  reorderChannels(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.selectedChannels, event.previousIndex, event.currentIndex);
    if (this.hasData && this.lastResult) {
      // Reordenar não muda o conjunto de canais: não refaz o /stats (a chave do painel
      // depende da ordem); a tabela de estatísticas pega a nova ordem no próximo refresh.
      this.renderChart(this.lastResult);
    }
  }

  /** Range REAL (sem padding) do canal a partir do último resultado plotado.
   *  Null quando ainda não há dado carregado para o canal. */
  private realRangeFor(channel: string): { min: number; max: number } | null {
    const series = this.lastResult?.series?.[channel];
    if (!series || series.length === 0) return null;
    return this.rawRange(series);
  }

  /** Escala efetiva do eixo Y do canal: override do usuário ou range real. */
  effectiveRange(channel: string): { min: number; max: number } | null {
    return this.channelRanges.get(channel) ?? this.realRangeFor(channel);
  }

  /** True quando há dado carregado para pré-preencher min/máx. */
  get canEditRanges(): boolean {
    return this.hasData && !!this.lastResult;
  }

  // minFor/maxFor devolvem na UNIDADE DE EXIBIÇÃO (bar/psi), já truncados (sem
  // arredondar) às casas decimais do canal; o armazenamento interno é sempre em bar cru.
  minFor(channel: string): number | null {
    const r = this.effectiveRange(channel);
    if (!r) return null;
    return this.displayBound(r.min, channel);
  }

  maxFor(channel: string): number | null {
    const r = this.effectiveRange(channel);
    if (!r) return null;
    return this.displayBound(r.max, channel);
  }

  /**
   * Bar -> exibição com truncagem. O ida-e-volta bar/psi perde 1 ULP (0.23 volta 0.22999999...)
   * e a truncagem derrubaria um centésimo do que o usuário digitou; o `toPrecision(12)` absorve
   * esse resíduo sem arredondar o valor de verdade.
   */
  private displayBound(bar: number, channel: string): number {
    const shown = Number((bar * this.unitFactorFor(channel)).toPrecision(12));
    return truncateTo(shown, this.decimalsFor(channel));
  }

  setMin(channel: string, value: number | string | null): void {
    this.setRangeBound(channel, 'min', value);
  }

  setMax(channel: string, value: number | string | null): void {
    this.setRangeBound(channel, 'max', value);
  }

  private setRangeBound(channel: string, bound: 'min' | 'max', value: number | string | null): void {
    if (!this.isSelected(channel)) return;
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    const base = this.channelRanges.get(channel) ?? this.realRangeFor(channel);
    if (!base) return;
    // Converte o valor digitado (unidade de exibição) de volta para bar.
    const nBase = n / this.unitFactorFor(channel);
    const next = { ...base, [bound]: nBase };
    // Garante min < max; se colidir, mantém uma folga mínima.
    if (next.min >= next.max) {
      if (bound === 'min') next.min = next.max - Math.max(Math.abs(next.max) * 0.01, 1e-6);
      else next.max = next.min + Math.max(Math.abs(next.min) * 0.01, 1e-6);
    }
    this.channelRanges.set(channel, next);
    if (this.hasData && this.lastResult) {
      this.renderChart(this.lastResult);
    }
  }

  /** Volta o canal ao range real (remove o override). */
  resetRange(channel: string): void {
    if (!this.channelRanges.has(channel)) return;
    this.channelRanges.delete(channel);
    if (this.hasData && this.lastResult) {
      this.renderChart(this.lastResult);
    }
  }

  /** Se o canal tem override manual de escala (para exibir o botão de reset). */
  hasRangeOverride(channel: string): boolean {
    return this.channelRanges.has(channel);
  }

  // ─── Unidades e pressão (bar ⇄ psi) ─────────────────────────────────────

  /**
   * Unidade canônica (armazenada) do canal: a do resultado plotado (`channel_metadata` do
   * Arrow) ou, antes disso, a do `channels_list`. null = desconhecida — nunca se inventa uma.
   */
  private canonicalUnitFor(channel: string): string | null {
    const fromResult = this.lastResult?.units?.[channel];
    if (fromResult !== undefined) return fromResult;
    return this.listedUnits.get(channel) ?? null;
  }

  /** Canal com unidade canônica 'bar': o único que o toggle bar/psi converte. */
  isPressureChannel(channel: string): boolean {
    return this.canonicalUnitFor(channel) === 'bar';
  }

  /** Há algum canal em bar na seleção (mostra o toggle de unidade). */
  get hasPressureSelected(): boolean {
    return this.selectedChannels.some((c) => this.isPressureChannel(c));
  }

  /** Fator base(bar)→exibição para o canal. 1 quando não é bar ou unidade = bar. */
  private unitFactorFor(channel: string): number {
    return this.isPressureChannel(channel) && this.mapUnit === 'psi' ? BAR_TO_PSI : 1;
  }

  /** Unidade mostrada do canal (já convertida quando o toggle está em psi); null = sem unidade. */
  unitLabelFor(channel: string): string | null {
    if (this.isPressureChannel(channel) && this.mapUnit === 'psi') return 'psi';
    return this.canonicalUnitFor(channel);
  }

  /** Sufixo de unidade (" bar", " °C"…) para rótulos; vazio quando a unidade é desconhecida. */
  private unitSuffixFor(channel: string): string {
    const unit = this.unitLabelFor(channel);
    return unit ? ` ${unit}` : '';
  }

  /** Troca da unidade (bar/psi): só re-renderiza — overrides seguem em bar. */
  onMapUnitChange(): void {
    if (this.hasData && this.lastResult) {
      this.renderChart(this.lastResult);
      this.refreshStatsInputs();
    }
  }

  /**
   * Entradas do painel de estatísticas. Nomes EXATOS do servidor (D6). Para os canais em bar,
   * com PSI escolhido, pede a projeção no servidor ({unit}): o /stats converte e
   * devolve a unidade, e o painel resolve canônico × projetado canal a canal (§12.3).
   * Nada é convertido duas vezes: o gráfico converte só a própria exibição.
   */
  private refreshStatsInputs(): void {
    const selected = [...this.selectedChannels];
    this.statsChannels = selected.map((name) => ({
      name: this.serverNameFor(name),
      label: this.labelFor(name),
      color: this.colorFor(name),
    }));
    const prefs: DisplayProjections = {};
    if (this.mapUnit === 'psi') {
      for (const name of selected) {
        if (this.isPressureChannel(name)) {
          // Só a unidade: o servidor já converte bar -> psi; um multiplier aqui projetaria 2 vezes.
          prefs[this.serverNameFor(name)] = { unit: 'psi' };
        }
      }
    }
    this.statsProjections = buildDisplayProjections(
      this.statsChannels.map((c) => c.name),
      prefs,
    ) ?? null;
  }

  groupFor(channel: string): number {
    return this.channelGroups.get(channel) ?? 1;
  }

  setGroup(channel: string, group: number): void {
    if (!this.isSelected(channel)) return;
    const clamped = Math.max(1, Math.min(MAX_GROUPS, Math.floor(group)));
    this.channelGroups.set(channel, clamped);
    if (this.hasData && this.lastResult) {
      this.renderChart(this.lastResult);
    }
  }

  cycleGroup(channel: string): void {
    const current = this.groupFor(channel);
    const next = (current % MAX_GROUPS) + 1;
    this.setGroup(channel, next);
  }

  toggleGroupedView(): void {
    this.groupedView = !this.groupedView;
    if (this.hasData && this.lastResult) {
      this.renderChart(this.lastResult);
    }
  }

  toggleFollowCursor(): void {
    this.followCursor = !this.followCursor;
    if (this.followCursor) {
      this.lastHoverX = null;
    }
    if (this.hasData && this.lastResult) {
      this.renderChart(this.lastResult);
    }
  }

  zoomIn(): void {
    this.applyZoom(ZOOM_FACTOR);
  }

  zoomOut(): void {
    this.applyZoom(1 / ZOOM_FACTOR);
  }

  zoomReset(): void {
    if (!this.chartInstance) return;
    this.chartInstance.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
  }

  private applyZoom(factor: number): void {
    if (!this.chartInstance) return;
    const opt = this.chartInstance.getOption() as { dataZoom?: Array<Record<string, unknown>> };
    const dz = (opt.dataZoom ?? [])[0] ?? {};
    const start = typeof dz['start'] === 'number' ? (dz['start'] as number) : 0;
    const end = typeof dz['end'] === 'number' ? (dz['end'] as number) : 100;
    const center = (start + end) / 2;
    const range = end - start;
    const newRange = Math.max(0.5, Math.min(100, range * factor));
    const newStart = Math.max(0, center - newRange / 2);
    const newEnd = Math.min(100, center + newRange / 2);
    this.chartInstance.dispatchAction({
      type: 'dataZoom',
      start: newStart,
      end: newEnd,
    });
  }

  // ─── Right-click context menu ───────────────────────────────────────────

  onChartContextMenu(event: MouseEvent): void {
    if (!this.chartInstance || !this.hasData) return;
    event.preventDefault();
    const rect = this.chartRef.nativeElement.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const dataPoint = this.chartInstance.convertFromPixel({ xAxisIndex: 0 }, offsetX);
    const chartX = typeof dataPoint === 'number' ? dataPoint : null;
    this.contextMenuChartX = chartX;
    this.contextMenuX = event.clientX;
    this.contextMenuY = event.clientY;
    this.contextMenuOpen = true;
  }

  closeContextMenu(): void {
    this.contextMenuOpen = false;
    this.contextMenuChartX = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.contextMenuOpen) this.closeContextMenu();
  }

  toggleCursorNames(): void {
    this.showCursorNames = !this.showCursorNames;
    this.closeContextMenu();
    if (this.hasData && this.lastResult) this.renderChart(this.lastResult);
  }

  toggleCursorValues(): void {
    this.showCursorValues = !this.showCursorValues;
    this.closeContextMenu();
    if (this.hasData && this.lastResult) this.renderChart(this.lastResult);
  }

  toggleCursorsVisible(): void {
    this.cursorsVisible = !this.cursorsVisible;
    this.closeContextMenu();
    if (this.hasData && this.lastResult) this.renderChart(this.lastResult);
  }

  positionCursor(slot: 1 | 2): void {
    const x = this.contextMenuChartX;
    if (x === null) {
      this.closeContextMenu();
      return;
    }
    if (slot === 1) this.cursor1X = x;
    else this.cursor2X = x;
    this.cursorsVisible = true;
    this.closeContextMenu();
    if (this.hasData && this.lastResult) this.renderChart(this.lastResult);
  }

  clearCursors(): void {
    this.cursor1X = null;
    this.cursor2X = null;
    this.closeContextMenu();
    if (this.hasData && this.lastResult) this.renderChart(this.lastResult);
  }

  isVisible(channel: string): boolean {
    return !this.hiddenChannels.has(channel);
  }

  toggleVisibility(channel: string): void {
    if (!this.isSelected(channel)) return;
    const wasVisible = this.isVisible(channel);
    if (wasVisible) {
      this.hiddenChannels.add(channel);
    } else {
      this.hiddenChannels.delete(channel);
    }
    if (this.chartInstance && this.hasData) {
      this.chartInstance.dispatchAction({
        type: wasVisible ? 'legendUnSelect' : 'legendSelect',
        name: channel,
      });
    }
  }

  decimalsFor(channel: string): number {
    return this.channelDecimals.get(channel) ?? defaultDecimalsFor(channel);
  }

  setDecimals(channel: string, value: number | string | null): void {
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    const clamped = Math.max(0, Math.min(MAX_DECIMALS, Math.floor(n)));
    this.channelDecimals.set(channel, clamped);
  }

  openDecimalsDialog(): void {
    this.showDecimalsDialog = true;
  }

  closeDecimalsDialog(): void {
    this.showDecimalsDialog = false;
  }

  /** Abre as configurações avançadas de um canal (engrenagem ou botão direito). */
  openChannelSettings(channel: string): void {
    if (!this.isSelected(channel)) return;
    this.settingsChannel = channel;
    this.showChannelSettings = true;
  }

  closeChannelSettings(): void {
    this.showChannelSettings = false;
    this.settingsChannel = null;
  }

  /** Botão direito sobre a linha de um canal selecionado → abre o modal dele. */
  onChannelRowContextMenu(event: MouseEvent, channel: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.openChannelSettings(channel);
  }

  resetDecimalsToDefault(): void {
    for (const channel of this.selectedChannels) {
      this.channelDecimals.set(channel, defaultDecimalsFor(channel));
    }
  }

  colorFor(channel: string): string {
    return this.channelColors.get(channel) ?? VISION_PALETTE[0];
  }

  onColorChange(channel: string, color: string): void {
    if (!this.channelColors.has(channel)) return;
    this.channelColors.set(channel, color);
    if (this.hasData && this.lastResult) {
      this.renderChart(this.lastResult);
      this.refreshStatsInputs();
    }
  }

  private nextAvailableColor(): string {
    const used = new Set(this.channelColors.values());
    const free = VISION_PALETTE.find((c) => !used.has(c));
    if (free) return free;
    return VISION_PALETTE[this.selectedChannels.length % VISION_PALETTE.length];
  }

  trackByName(_index: number, name: string): string {
    return name;
  }

  goBack(): void {
    this.router.navigate(['/archives']);
  }

  /**
   * @param fromUser true quando veio do botão "Visualizar". Em tela estreita a sidebar
   * fica abaixo do gráfico, então o botão está longe do resultado: ao terminar, rolamos
   * o gráfico de volta à vista. A plotagem automática da abertura não rola (a página
   * já está no topo).
   */
  plot(fromUser = false): void {
    if (this.isMapOnly) return; // mapa standalone não tem canais de log
    if (this.selectedChannels.length === 0 || this.selectedChannels.length > MAX_CHANNELS) return;
    if (!this.userID || !this.fileID) return;

    this.scrollChartIntoViewOnResult = fromUser && window.innerWidth <= 900;
    this.loading = true;
    this.error = null;

    // Dispara o /map em paralelo na primeira vez que o usuário clica em View.
    // O /map também converte o log pro parquet, então roda lado a lado com
    // /channels (que vira cache-hit quando ambos terminam). Não rebuscamos se
    // o mapa já está em memória — mudança de canais não invalida o mapa.
    if (!this.injectionMap && !this.mapLoading && !this.mapError) {
      this.loadMap();
    }

    // Com o channels_list os nomes seguem EXATAMENTE como o servidor os listou (D6). No
    // fallback (catálogo VisionFT estático) o .ftml recebe o alias PowerFT, e
    // normalizeAliasedResult devolve as chaves ao nome VisionFT usado nas Maps internas.
    const userSelection = [...this.selectedChannels];
    const requestSet = new Set<string>(userSelection.map((name) => this.serverNameFor(name)));

    this.channelsService
      .read(this.userID, this.fileID, [...requestSet], {
        fileType: this.channelsFileType,
        // lttb desligado: backend devolve resolução cheia (sem decimação),
        // pra linha ficar tão detalhada quanto no Vision.
        lttb: false,
        maxPoints: 50000,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => this.handleResult(
          this.catalogFromList ? result : this.normalizeAliasedResult(result, userSelection),
        ),
        error: (err: FileChannelsError) => this.handleError(err),
      });
  }

  /**
   * Nome enviado ao servidor para o canal. Com o `channels_list` é o próprio nome (D6); só no
   * fallback do `.ftml` o nome VisionFT é traduzido para o alias PowerFT.
   */
  private serverNameFor(name: string): string {
    if (this.catalogFromList || this.fileType !== 'ftml') return name;
    return VISIONFT_TO_POWERFT[name] ?? name;
  }

  /**
   * (Só no fallback.) Renomeia chaves PowerFT de volta para o nome VisionFT que o usuario
   * selecionou e recalcula a lista de canais ausentes.
   */
  private normalizeAliasedResult(
    result: FileChannelsResult,
    userSelection: string[],
  ): FileChannelsResult {
    const seriesKeys = new Set(Object.keys(result.series));
    const normalizedSeries: FileChannelsResult['series'] = {};
    const normalizedUnits: FileChannelsResult['units'] = {};
    const stillMissing: string[] = [];

    for (const name of userSelection) {
      let key: string | null = null;
      if (seriesKeys.has(name)) key = name;
      else {
        const alias = VISIONFT_TO_POWERFT[name];
        if (alias && seriesKeys.has(alias)) key = alias;
      }
      if (key === null) {
        stillMissing.push(name);
        continue;
      }
      normalizedSeries[name] = result.series[key];
      if (key in result.units) normalizedUnits[name] = result.units[key];
    }

    // Mantem o resto do payload, mas com chaves normalizadas e missing recalculado
    return {
      ...result,
      series: normalizedSeries,
      units: normalizedUnits,
      missing: stillMissing,
    };
  }

  private handleResult(result: FileChannelsResult): void {
    this.loading = false;
    this.missing = result.missing;
    this.rows = result.rows;
    this.hasData = true;
    this.lastResult = result;
    this.refreshStatsInputs();

    if (this.missing.length > 0) {
      this.translocoService
        .selectTranslateObject('log.channels')
        .pipe(takeUntil(this.destroy$))
        .subscribe((t: Record<string, string>) => {
          this.messageService.add({
            key: 'notification',
            severity: 'warn',
            summary: t['missingWarning'] ?? 'Canais ignorados',
            detail: this.missingLabels,
            life: 8000,
          });
        });
    }

    this.renderChart(result);

    if (this.scrollChartIntoViewOnResult) {
      this.scrollChartIntoViewOnResult = false;
      this.chartRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private handleError(err: FileChannelsError): void {
    this.loading = false;
    this.hasData = false;
    this.lastResult = null;

    // Degradação graciosa (contrato §6): se o MAPA chegou íntegro, não deixa a falha do log
    // esconder tudo. Em modo 'log' o CSS faz `.map-section { display: none }`, então além do
    // *ngIf é preciso trocar o modo — senão o mapa fica invisível por CSS.
    const degradouParaMapa = this.injectionMaps.length > 0 && this.displayMode === 'log';
    if (degradouParaMapa) this.displayMode = 'map';

    const key = this.errorKeyFor(err.errorCode);
    this.translocoService
      .selectTranslate(key)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.error = msg;
        // Em `mode-map` o CSS esconde `.state-overlay`, então o erro do log ficaria invisível
        // justamente quando degradamos para o mapa. O toast garante que o usuário saiba.
        if (degradouParaMapa) {
          this.messageService.add({
            key: 'notification',
            severity: 'warn',
            summary: this.translocoService.translate('log.channels.toast.logUnavailable'),
            detail: msg,
            life: 8000,
          });
        }
      });
  }

  private errorKeyFor(code: string): string {
    switch (code) {
      case 'INVALID_JSON_SCHEMA':
        return 'log.channels.errorInvalidRequest';
      case 'FILE_NOT_FOUND':
        return 'log.channels.errorFileNotFound';
      case 'CONVERSION_FAILED':
        return 'log.channels.errorConversionFailed';
      // Estava faltando: sem este case o 422 caía em "falha de comunicação" (contrato §6).
      case 'MAP_PASSWORD_PROTECTED':
        return 'log.channels.errorPasswordProtected';
      case 'INTERNAL_ERROR':
        return 'log.channels.errorInternal';
      default:
        return 'log.channels.errorNetwork';
    }
  }

  private renderChart(result: FileChannelsResult): void {
    if (!this.chartInstance) return;

    const xs = result.sampleTime;
    // Ordem dos eixos segue a ordem escolhida pelo usuário (drag na sidebar);
    // canais no resultado que não estejam na seleção (defensivo) vão ao fim.
    const present = new Set(Object.keys(result.series));
    const ordered = this.selectedChannels.filter((n) => present.has(n));
    for (const n of present) if (!ordered.includes(n)) ordered.push(n);
    const seriesNames = ordered;
    // Escala Y: override do usuário (mín/máx do diálogo) ou range real do dado,
    // convertido para a unidade de exibição do canal (bar/psi p/ pressão).
    const ranges = seriesNames.map((name) => {
      const f = this.unitFactorFor(name);
      const base = this.channelRanges.get(name) ?? this.rawRange(result.series[name]);
      // Série sem amostra finita: eixo sem min/max (nenhuma escala inventada).
      return base ? { min: base.min * f, max: base.max * f } : null;
    });

    // ─── Group / grid layout ────────────────────────────────────────────
    const channelGroup = (name: string) =>
      this.groupedView ? this.groupFor(name) : 1;

    const effectiveGroups = Array.from(
      new Set(seriesNames.map(channelGroup)),
    ).sort((a, b) => a - b);
    const numGrids = Math.max(1, effectiveGroups.length);

    const groupToGridIdx = new Map<number, number>();
    effectiveGroups.forEach((g, idx) => groupToGridIdx.set(g, idx));

    const TOP_PCT = 4;
    const BOTTOM_PCT = 18;
    const GAP_PCT = 4;
    const usable = 100 - TOP_PCT - BOTTOM_PCT;
    const gridH = (usable - GAP_PCT * (numGrids - 1)) / numGrids;
    const grids = Array.from({ length: numGrids }, (_, idx) => ({
      left: 56,
      right: 16,
      top: `${TOP_PCT + idx * (gridH + GAP_PCT)}%`,
      height: `${gridH}%`,
    }));

    // First series in each grid → shows the y-axis labels
    const firstSeriesIdxInGrid = new Set<number>();
    const seenGrids = new Set<number>();
    for (let i = 0; i < seriesNames.length; i++) {
      const gIdx = groupToGridIdx.get(channelGroup(seriesNames[i])) ?? 0;
      if (!seenGrids.has(gIdx)) {
        seenGrids.add(gIdx);
        firstSeriesIdxInGrid.add(i);
      }
    }

    // ─── Cursor lines + inline labels ───────────────────────────────────
    const cursorXs: Array<{ x: number; slot: 1 | 2 }> = [];
    if (this.cursorsVisible) {
      if (this.cursor1X !== null) cursorXs.push({ x: this.cursor1X, slot: 1 });
      if (this.cursor2X !== null) cursorXs.push({ x: this.cursor2X, slot: 2 });
    }

    // ─── Series ─────────────────────────────────────────────────────────
    const series = seriesNames.map((name, idx) => {
      const values = result.series[name];
      const f = this.unitFactorFor(name); // conversão de unidade (pressão)
      const n = values.length;
      // NaN = amostra ausente (nulo no Arrow): vira null para o ECharts abrir uma lacuna
      // em vez de desenhar zero (D8).
      const data = new Array<[number, number | null]>(n);
      for (let i = 0; i < n; i++) {
        const v = values[i];
        data[i] = [xs[i], Number.isFinite(v) ? v * f : null];
      }
      const color = this.channelColors.get(name) ?? VISION_PALETTE[0];
      const gIdx = groupToGridIdx.get(channelGroup(name)) ?? 0;
      const isFirstInGrid = firstSeriesIdxInGrid.has(idx);

      // markLine: vertical cursors 1/2 — only attached to the first series in
      // each grid so each cursor is drawn once per grid (avoids duplicates).
      const markLineData = isFirstInGrid
        ? cursorXs.map(({ x, slot }) => ({
            xAxis: x,
            label: {
              show: true,
              position: 'insideEndTop',
              formatter: `C${slot}`,
              color: '#fff',
              backgroundColor: slot === 1 ? '#2D9CDB' : '#FF8A00',
              padding: [2, 6],
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              fontFamily: 'Saira, sans-serif',
            },
            lineStyle: {
              color: slot === 1 ? '#2D9CDB' : '#FF8A00',
              type: 'solid',
              width: 1.5,
            },
          }))
        : [];

      // markPoint: inline labels at cursor positions for this series
      const markPointData: Array<Record<string, unknown>> = [];
      if (
        (this.showCursorNames || this.showCursorValues) &&
        !this.hiddenChannels.has(name)
      ) {
        for (const { x } of cursorXs) {
          const yRaw = this.valueAt(values, xs, x);
          if (yRaw === null) continue;
          // Amostra ausente: o rótulo mostra '—' e o ponto some (sem símbolo), ancorado no
          // piso do eixo só para o rótulo ter onde ficar.
          const absent = !Number.isFinite(yRaw);
          const y = absent ? (ranges[idx]?.min ?? 0) : yRaw * f; // mesma unidade de exibição da série
          const yShown = absent ? null : yRaw * f;
          markPointData.push({
            coord: [x, y],
            symbol: 'circle',
            symbolSize: absent ? 0 : 6,
            itemStyle: { color },
            label: {
              show: true,
              formatter: this.buildCursorLabel(name, yShown),
              position: 'right',
              distance: 8,
              color: '#fff',
              backgroundColor: 'rgba(18, 18, 18, 0.92)',
              borderColor: color,
              borderWidth: 1,
              borderRadius: 4,
              padding: [3, 6],
              fontSize: 11,
              fontFamily: 'Consolas, monospace',
            },
          });
        }
      }

      return {
        name,
        type: 'line' as const,
        xAxisIndex: gIdx,
        yAxisIndex: idx,
        showSymbol: false,
        // Sem decimação: desenha todos os pontos (fidelidade igual ao Vision).
        // O arquivo cabe em memória; sem 'lttb'/'large' a linha mantém detalhe.
        lineStyle: { color, width: 1 },
        itemStyle: { color },
        emphasis: { lineStyle: { width: 1.5 } },
        data,
        ...(markLineData.length > 0
          ? { markLine: { silent: true, symbol: ['none', 'none'], data: markLineData } }
          : {}),
        ...(markPointData.length > 0
          ? { markPoint: { silent: true, data: markPointData } }
          : {}),
      };
    });

    // ─── Axes ───────────────────────────────────────────────────────────
    // Fixa o limite direito no fim real do log + 1s. Sem isso, com LTTB ligado
    // o ECharts arredonda para um "nice number" muito alem do ultimo Sample_Time
    // e cria uma faixa vazia gigante na direita do grafico.
    const xMin = xs.length > 0 ? Math.min(0, xs[0]) : 0;
    const xMax = xs.length > 0 ? xs[xs.length - 1] + 1 : 1;
    const xAxes = Array.from({ length: numGrids }, (_, idx) => ({
      type: 'value' as const,
      gridIndex: idx,
      min: xMin,
      max: xMax,
      axisLine: { show: true, lineStyle: { color: '#474747' } },
      axisTick: { show: false },
      axisLabel: {
        show: idx === numGrids - 1,
        color: '#9b9b9b',
        fontSize: 11,
        fontFamily: 'Consolas, monospace',
        formatter: (v: number) => this.formatTimeAxis(v, this.timeDecimals()),
      },
      splitLine: { lineStyle: { color: '#303030' } },
    }));

    const yAxes = seriesNames.length > 0
      ? seriesNames.map((name, idx) => {
          const range = ranges[idx];
          const decimals = this.decimalsFor(name);
          const visible = firstSeriesIdxInGrid.has(idx);
          const color = this.channelColors.get(name) ?? VISION_PALETTE[0];
          const gIdx = groupToGridIdx.get(channelGroup(name)) ?? 0;
          return {
            type: 'value' as const,
            gridIndex: gIdx,
            ...(range ? { min: range.min, max: range.max } : {}),
            scale: true,
            // Unidade ao lado do eixo; desconhecida (null) não mostra nada.
            name: visible ? (this.unitLabelFor(name) ?? '') : '',
            nameLocation: 'end' as const,
            nameGap: 8,
            nameTextStyle: {
              color,
              fontSize: 10,
              fontFamily: 'Consolas, monospace',
              align: 'left' as const,
            },
            axisLine: { show: visible, lineStyle: { color } },
            axisTick: { show: false },
            axisLabel: {
              show: visible && !!range,
              color: visible ? color : 'transparent',
              fontSize: 11,
              fontFamily: 'Consolas, monospace',
              formatter: (v: number) => this.formatNumber(v, decimals),
            },
            splitLine: {
              show: visible,
              lineStyle: { color: '#303030' },
            },
          };
        })
      : [
          {
            type: 'value' as const,
            gridIndex: 0,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { lineStyle: { color: '#303030' } },
          },
        ];

    // ─── setOption ──────────────────────────────────────────────────────
    this.chartInstance.setOption(
      {
        animation: false,
        backgroundColor: '#0b0c0e',
        textStyle: { color: '#9b9b9b', fontFamily: 'Saira, sans-serif' },
        grid: grids,
        axisPointer: {
          link: [{ xAxisIndex: 'all' }],
        },
        tooltip: {
          trigger: 'axis',
          triggerOn: this.followCursor ? 'mousemove' : 'click',
          // 0ms = sem throttle (default ~30ms gera o "gap" perceptível ao arrastar rápido)
          // transitionDuration: 0 → tooltip salta direto pra nova posição sem animação
          throttle: 0,
          transitionDuration: 0,
          // appendTo body evita re-layout do container do chart a cada show/hide
          appendToBody: true,
          axisPointer: {
            type: 'cross',
            // snap: false → pointer segue o cursor 1:1 em vez de pular pro nearest data point
            snap: false,
            animation: false,
            lineStyle: { color: '#ffffff', width: 1, type: 'solid' },
            crossStyle: { color: '#ffffff', width: 1 },
            label: {
              backgroundColor: '#303030',
              borderColor: '#9b9b9b',
              color: '#fff',
              fontFamily: 'Consolas, monospace',
              formatter: (p: { value?: number | string; axisDimension?: string }) => {
                const v = typeof p.value === 'number' ? p.value : Number(p.value);
                if (!Number.isFinite(v)) return String(p.value ?? '');
                return p.axisDimension === 'x'
                  ? this.formatTimeAxis(v, this.timeDecimals())
                  : this.formatNumber(v, 2);
              },
            },
          },
          backgroundColor: 'rgba(18, 18, 18, 0.92)',
          borderColor: '#9b9b9b',
          textStyle: { color: '#fff', fontSize: 11 },
          formatter: (params: unknown) => this.formatTooltip(params),
        },
        legend: {
          show: false,
          data: seriesNames,
          selected: Object.fromEntries(
            seriesNames.map((n) => [n, !this.hiddenChannels.has(n)]),
          ),
        },
        xAxis: xAxes,
        yAxis: yAxes,
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: xAxes.map((_, idx) => idx),
            // Disable ALL native mouse interactions on the chart area.
            // Pan/zoom are driven entirely by our custom mouse handlers
            // (left=area-zoom, right=horizontal-pan) and by the slider below.
            disabled: true,
          },
          {
            type: 'slider',
            xAxisIndex: xAxes.map((_, idx) => idx),
            height: 28,
            bottom: 12,
            backgroundColor: '#0b0c0e',
            borderColor: '#2a2c31',
            fillerColor: 'rgba(0, 255, 0, 0.18)',
            dataBackground: {
              lineStyle: { color: '#00FF00', width: 1 },
              areaStyle: { color: 'rgba(0, 255, 0, 0.15)' },
            },
            selectedDataBackground: {
              lineStyle: { color: '#00FF00', width: 1 },
              areaStyle: { color: 'rgba(0, 255, 0, 0.35)' },
            },
            handleStyle: { color: '#00FF00', borderColor: '#00FF00' },
            moveHandleStyle: { color: '#00FF00' },
            textStyle: { color: '#9b9b9b', fontSize: 10, fontFamily: 'Consolas, monospace' },
          },
        ],
        series,
      },
      true,
    );

    setTimeout(() => this.chartInstance?.resize(), 0);
  }

  private buildCursorLabel(channel: string, value: number | null): string {
    const name = this.labelFor(channel);
    const formatted = value === null
      ? ABSENT_TEXT
      : this.formatNumber(value, this.decimalsFor(channel)) + this.unitSuffixFor(channel);
    if (this.showCursorNames && this.showCursorValues) return `${name}: ${formatted}`;
    if (this.showCursorNames) return name;
    if (this.showCursorValues) return formatted;
    return '';
  }

  private valueAt(
    values: ArrayLike<number>,
    sampleTime: ArrayLike<number>,
    x: number,
  ): number | null {
    const n = sampleTime.length;
    if (n === 0) return null;
    if (x <= sampleTime[0]) return values[0];
    if (x >= sampleTime[n - 1]) return values[n - 1];
    let lo = 0;
    let hi = n - 1;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (sampleTime[mid] < x) lo = mid + 1;
      else hi = mid;
    }
    return values[lo];
  }

  private formatTooltip(params: unknown): string {
    const arr = Array.isArray(params) ? params : [params];
    if (arr.length === 0) return '';

    // O tempo exibido é o da amostra mostrada: a posição crua do mouse não é um instante do log.
    const first = arr[0] as { axisValue?: number; data?: [number, number | null] };
    const time = typeof first.data?.[0] === 'number'
      ? first.data[0]
      : (first.axisValue ?? 0);
    const header = `<div style="font-size:11px;color:#9b9b9b;margin-bottom:4px;font-family:Consolas,monospace;">${this.formatNumber(time, 2)}</div>`;

    const rows = arr.map((raw) => {
      const p = raw as { seriesName: string; color: string; data: [number, number | null] };
      const value = p.data?.[1];
      const decimals = this.decimalsFor(p.seriesName);
      const formatted = value !== null && value !== undefined && Number.isFinite(value)
        ? this.formatNumber(value, decimals) + this.escapeHtml(this.unitSuffixFor(p.seriesName))
        : ABSENT_TEXT;
      const label = this.escapeHtml(this.labelFor(p.seriesName));
      const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px;vertical-align:middle;"></span>`;
      return (
        `<div style="display:flex;align-items:center;justify-content:space-between;gap:18px;font-family:Saira,sans-serif;font-size:12px;line-height:1.6;">` +
        `<span>${dot}${label}</span>` +
        `<span style="font-weight:600;color:#fff;font-family:Consolas,monospace;">${formatted}</span>` +
        `</div>`
      );
    });
    return header + rows.join('');
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Formata o eixo X (segundos) como o Vision: "20s" abaixo de 1 min,
   *  "m:ss" a partir de 1 min. Trunca (nunca arredonda) para o segundo inteiro, D3. */
  private formatTimeAxis(seconds: number, decimals = 0): string {
    if (decimals > 0 && seconds >= 0 && seconds < 60) {
      // Janela curta: mantém frações (cortadas, D3) para os ticks continuarem distintos.
      return `${formatTruncated(seconds, { minDecimals: decimals, maxDecimals: decimals, ...SCREEN_NUMBER_FORMAT })}s`;
    }
    const total = Math.trunc(seconds);
    if (total < 60) return `${total}s`;
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  /**
   * Casas dos rótulos de tempo conforme a janela VISÍVEL (zoom aplicado): 0 a partir de 10 s,
   * 1 de 2 a 10 s, 2 abaixo disso. A janela sai de convertFromPixel nas bordas do grid.
   */
  private timeDecimals(): number {
    const chart = this.chartInstance;
    if (!chart) return 0;
    const left = chart.convertFromPixel({ xAxisIndex: 0 }, 56);
    const right = chart.convertFromPixel({ xAxisIndex: 0 }, chart.getWidth() - 16);
    if (typeof left !== 'number' || typeof right !== 'number') return 0;
    const span = Math.abs(right - left);
    if (!Number.isFinite(span) || span >= 10) return 0;
    return span >= 2 ? 1 : 2;
  }

  private formatNumber(value: number, decimals: number): string {
    // Trunca (nunca arredonda) e limita a 3 casas; ponto decimal e sem agrupamento (D1).
    return formatTruncated(value, { minDecimals: decimals, maxDecimals: decimals, ...SCREEN_NUMBER_FORMAT });
  }

  /** Min/máx exatos do dado (sem padding). Usado como escala real e para
   *  pré-preencher os campos de mín/máx do diálogo de configuração.
   *  Null quando não há nenhuma amostra finita. */
  private rawRange(values: ArrayLike<number>): { min: number; max: number } | null {
    const n = values.length;
    if (n === 0) return null;

    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < n; i++) {
      const v = values[i];
      if (!Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }

    // Nenhuma amostra finita (tudo null/NaN): não existe escala a inventar (D8).
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    if (min === max) {
      const delta = Math.abs(min) > 1 ? Math.abs(min) * 0.1 : 1;
      return { min: min - delta, max: max + delta };
    }
    return { min, max };
  }
}
